import type Graph from 'graphology';

const FALLBACK = 'flowchart TD\n  empty["No steps"]';
const MAX_LABEL = 40;

/**
 * Walk BELONGS_TO_PROCESS to collect step nodes attached to a Process, then
 * emit `flowchart TD` Mermaid syntax with CALLS edges between steps. Falls
 * back to a linear chain when no CALLS edges exist between steps.
 */
export function generateProcessMermaid(graph: Graph, processNodeId: string): string {
  if (!graph.hasNode(processNodeId)) return FALLBACK;

  const stepIds = new Set<string>();
  graph.forEachInEdge(processNodeId, (_key, attrs, source) => {
    if (attrs.relationType === 'BELONGS_TO_PROCESS') stepIds.add(source);
  });
  if (stepIds.size === 0) return FALLBACK;

  const lines: string[] = ['flowchart TD'];
  lines.push(
    '  classDef step fill:#1e293b,stroke:#22d3ee,stroke-width:2px,color:#f8fafc;',
    '  classDef entry fill:#064e3b,stroke:#34d399,stroke-width:3px,color:#f8fafc;',
    '  classDef terminal fill:#831843,stroke:#f472b6,stroke-width:3px,color:#f8fafc;',
  );

  const idMap = new Map<string, string>();
  const sortedSteps = [...stepIds].sort();
  sortedSteps.forEach((id, i) => idMap.set(id, `n${i}`));

  const edges: [string, string][] = [];
  for (const stepId of stepIds) {
    graph.forEachOutEdge(stepId, (_key, attrs, _source, target) => {
      if (attrs.relationType !== 'CALLS') return;
      if (!stepIds.has(target)) return;
      if (target === stepId) return;
      edges.push([stepId, target]);
    });
  }

  const incoming = new Set(edges.map(([, t]) => t));
  const outgoing = new Set(edges.map(([s]) => s));

  for (const stepId of sortedSteps) {
    const safeId = idMap.get(stepId)!;
    const label = sanitizeLabel(getNodeLabel(graph, stepId));
    let cls = 'step';
    if (!incoming.has(stepId)) cls = 'entry';
    else if (!outgoing.has(stepId)) cls = 'terminal';
    lines.push(`  ${safeId}["${label}"]:::${cls}`);
  }

  if (edges.length === 0) {
    for (let i = 0; i < sortedSteps.length - 1; i++) {
      const a = idMap.get(sortedSteps[i]!) ?? '';
      const b = idMap.get(sortedSteps[i + 1]!) ?? '';
      lines.push(`  ${a} --> ${b}`);
    }
  } else {
    for (const [s, t] of edges) {
      const a = idMap.get(s) ?? '';
      const b = idMap.get(t) ?? '';
      lines.push(`  ${a} --> ${b}`);
    }
  }

  return lines.join('\n');
}

function getNodeLabel(graph: Graph, id: string): string {
  const attrs = graph.getNodeAttributes(id) as Record<string, unknown>;
  return (attrs.label as string) ?? (attrs.filePath as string) ?? id;
}

function sanitizeLabel(text: string): string {
  const cleaned = text
    .replace(/["[\]<>{}()|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.length > MAX_LABEL ? `${cleaned.slice(0, MAX_LABEL - 1)}…` : cleaned;
}
